import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '@/lib/analytics';

// Mount once inside <BrowserRouter>. useLocation throws otherwise.
export function usePageView(): void {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    track('Page Viewed', {
      path: sanitizePath(location.pathname),
      route_name: pathToRouteName(location.pathname),
    });
  }, [location.pathname]);
}

export function pathToRouteName(pathname: string): string {
  const p = pathname.replace(/\/+$/, '').toLowerCase() || '/';

  const staticMap: Record<string, string> = {
    '/': 'home',
    '/auth': 'auth',
    '/therapist-auth': 'therapist-auth',
    '/therapist-signup': 'therapist-signup',
    '/therapist-subscription-required': 'therapist-subscription-required',
    '/therapist-dashboard': 'therapist-dashboard',
    '/therapist-profile': 'therapist-profile',
    '/therapist-analytics': 'therapist-analytics',
    '/dashboard-selector': 'dashboard-selector',
    '/onboarding': 'onboarding',
    '/verify-email': 'verify-email',
    '/install': 'install',
    '/privacy': 'privacy',
    '/privacy-policy': 'privacy-policy',
    '/terms': 'terms',
    '/terms-of-service': 'terms-of-service',
    '/provider-agreement': 'provider-agreement',
    '/about': 'about',
    '/faq': 'faq',
    '/contact': 'contact',
    '/app': 'app/home',
    '/app/discovery': 'app/discovery',
    '/app/messages': 'app/messages',
    '/app/profile': 'app/profile',
    '/app/settings': 'app/settings',
    '/app/my-vents': 'app/my-vents',
    '/app/bookmarks': 'app/bookmarks',
    '/app/therapists': 'app/therapists',
    '/app/support': 'app/support',
    '/app/feedback': 'app/feedback',
    '/app/admin': 'app/admin',
    '/app/admin/moderation': 'app/admin/moderation',
    '/app/admin/support': 'app/admin/support',
    '/app/admin/therapist-verification': 'app/admin/therapist-verification',
    '/app/admin/knowledge-base': 'app/admin/knowledge-base',
    '/app/admin/analytics': 'app/admin/analytics',
    '/app/admin/errors': 'app/admin/errors',
    '/app/admin/jobs': 'app/admin/jobs',
    '/app/admin/holds': 'app/admin/holds',
  };
  if (staticMap[p]) return staticMap[p];

  if (p.startsWith('/app/user/') && p.endsWith('/vents')) return 'app/user/vents';
  if (p.startsWith('/app/user/')) return 'app/user';
  if (p.startsWith('/app/rooms/')) return 'app/room';
  if (p.startsWith('/app/vent/')) return 'app/vent';
  if (p.startsWith('/app/therapist/payment-success')) return 'app/therapist/payment-success';
  if (p.startsWith('/app/therapist/')) return 'app/therapist';
  if (p.startsWith('/app/articles/')) return 'app/article';
  if (p.startsWith('/therapist-dashboard/articles/new')) return 'therapist-dashboard/articles/new';
  if (p.startsWith('/therapist-dashboard/articles/') && p.endsWith('/edit')) return 'therapist-dashboard/articles/edit';
  if (p.startsWith('/therapist-dashboard/articles')) return 'therapist-dashboard/articles';

  return 'unknown';
}

function sanitizePath(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ':id';
      if (/^\d+$/.test(seg)) return ':id';
      return seg;
    })
    .join('/');
}
